/**
 * Core GA4 DataLayer Tracking Implementation
 * Handles event queueing, de-duplication, consent mode, and dataLayer push
 */
import { AnalyticsEvent, ConsentState } from './types'
import { validateEvent, sanitizeEvent } from './validators'
// Global dataLayer queue
declare global {
  interface Window {
    dataLayer: any[]
    gtag?: (...args: any[]) => void
  }
}
// Event cache for de-duplication
const eventCache = new Map<string, string>()
const CACHE_TTL = 5000 // 5 seconds
// Event queue for when dataLayer is not ready
let eventQueue: AnalyticsEvent[] = []
let isDataLayerReady = false
// Consent state
let consentState: ConsentState = {
  analytics_storage: 'denied',
  ad_storage: 'denied',
}
/**
 * Initialize dataLayer and set up consent mode
 */
export function initDataLayer() {
  if (typeof window === 'undefined') return
  // Initialize dataLayer array
  if (!window.dataLayer) {
    window.dataLayer = []
  }
  // Set up gtag function
  window.gtag =
    window.gtag ||
    function () {
      window.dataLayer.push(arguments)
    }
  // Mark as ready
  isDataLayerReady = true
  // Flush queued events
  if (eventQueue.length > 0) {
    console.log(`[Analytics] Flushing ${eventQueue.length} queued events`)
    eventQueue.forEach((event) => pushToDataLayer(event))
    eventQueue = []
  }
}
/**
 * Update consent state
 */
export function updateConsent(consent: Partial<ConsentState>) {
  consentState = { ...consentState, ...consent }
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('consent', 'update', consentState)
  }
}
/**
 * Get current consent state
 */
export function getConsentState(): ConsentState {
  return { ...consentState }
}
/**
 * Generate UUID v4
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}
/**
 * Generate ISO 8601 timestamp
 */
function generateTimestamp(): string {
  return new Date().toISOString()
}
/**
 * Generate event ID for de-duplication
 */
function generateEventCacheKey(event: AnalyticsEvent): string {
  return `${event.event}_${JSON.stringify(event)}`
}
/**
 * Check if event is duplicate
 */
function isDuplicate(event: AnalyticsEvent): boolean {
  const cacheKey = generateEventCacheKey(event)
  const now = Date.now()
  // Check if we've seen this exact event recently
  const cachedTime = eventCache.get(cacheKey)
  if (cachedTime) {
    const age = now - parseInt(cachedTime, 10)
    if (age < CACHE_TTL) {
      console.warn(
        '[Analytics] Duplicate event detected, skipping:',
        event.event,
      )
      return true
    }
  }
  // Cache this event
  eventCache.set(cacheKey, now.toString())
  // Clean up old entries
  for (const [key, time] of eventCache.entries()) {
    if (now - parseInt(time, 10) > CACHE_TTL) {
      eventCache.delete(key)
    }
  }
  return false
}
/**
 * Push event to dataLayer
 */
function pushToDataLayer(event: AnalyticsEvent) {
  if (typeof window === 'undefined') return
  // Check consent
  if (consentState.analytics_storage === 'denied') {
    console.warn(
      '[Analytics] Analytics consent denied, event not sent:',
      event.event,
    )
    return
  }
  // Validate event
  if (!validateEvent(event)) {
    console.error('[Analytics] Event validation failed:', event)
    return
  }
  // Sanitize event
  const sanitized = sanitizeEvent(event)
  // Push to dataLayer
  window.dataLayer.push(sanitized)
  console.log('[Analytics] Event pushed:', sanitized.event, sanitized)
}
/**
 * Main tracking function
 */
export function track<T extends AnalyticsEvent>(
  eventName: T['event'],
  payload: Omit<T, 'event'>,
): void {
  // Create full event object
  const event = {
    event: eventName,
    ...payload,
  } as T
  // Check for duplicates
  if (isDuplicate(event)) {
    return
  }
  // Queue if dataLayer not ready
  if (!isDataLayerReady) {
    console.log('[Analytics] DataLayer not ready, queueing event:', eventName)
    eventQueue.push(event)
    return
  }
  // Push to dataLayer
  pushToDataLayer(event)
}
/**
 * Add base metadata to events that require it
 */
export function addBaseMetadata<T extends object>(
  payload: T,
): T & {
  event_id: string
  event_time: string
  app_platform: 'web'
} {
  return {
    ...payload,
    event_id: generateUUID(),
    event_time: generateTimestamp(),
    app_platform: 'web',
  }
}
/**
 * Get page context
 */
export function getPageContext(): {
  page_locale: 'ar-SA' | 'en-SA'
  page_location: string
  page_referrer: string
  page_title: string
} {
  if (typeof window === 'undefined') {
    return {
      page_locale: 'en-SA',
      page_location: '',
      page_referrer: '',
      page_title: '',
    }
  }
  // Detect locale from URL or document
  const pathname = window.location.pathname
  const isArabic =
    pathname.startsWith('/ar') || document.documentElement.lang === 'ar'
  return {
    page_locale: isArabic ? 'ar-SA' : 'en-SA',
    page_location: window.location.href,
    page_referrer: document.referrer,
    page_title: document.title,
  }
}
/**
 * Hash customer ID (SHA-256)
 */
export async function hashCustomerId(customerId: string): Promise<string> {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    console.warn(
      '[Analytics] Web Crypto API not available, returning unhashed ID',
    )
    return customerId
  }
  const encoder = new TextEncoder()
  const data = encoder.encode(customerId)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}
// Initialize on DOM ready
if (typeof window !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDataLayer)
  } else {
    initDataLayer()
  }
}
